// Правило дублирования блюда/приёма:
// {today, tomorrow, weekdays, alldays, anchor_date}.
// anchor_date — дата оригинала (YYYY-MM-DD); все варианты считаются от неё,
// а не от сегодняшнего дня: «каждый день на неделе» — это 7 суток вперёд от
// блюда, и правило не «уползает», если попап открыли из копии на другой дате.
//
// «Сегодня» (today) — копия на сам якорь. Смысл есть только у блюда из
// прошлого: его якорь подтягивается к сегодняшнему дню (effectiveAnchorDate),
// и «сегодня» — это повтор вчерашнего обеда сегодня же. Пользователи просили
// именно это: у вчерашнего приёма в попапе было только «завтра». У
// сегодняшних и будущих блюд вариант не показывается — копия на ту же дату
// бессмысленна.
//
// Пересечения вариантов схлопываются: «каждый день» включает будни, «завтра»
// может совпасть и с тем и с другим — набор дат строится через Set.

function parseDate(str) {
  return new Date(str + 'T00:00:00')
}

function fmtDate(d) {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

function addDays(dateStr, n) {
  const d = parseDate(dateStr)
  d.setDate(d.getDate() + n)
  return d
}

export function todayDateStr() {
  return fmtDate(new Date())
}

// Начало сегодняшних суток пользователя в ISO — для POST /diary/duplicate
// (поле not_before). Сервер не создаёт копии в прошлом, а копия «на сегодня»
// ложится на время вчерашнего приёма, которое сегодня уже могло пройти.
// Часовой пояс знает только клиент, поэтому границу суток присылает он.
export function todayStartIso(todayStr = fmtDate(new Date())) {
  return parseDate(todayStr).toISOString()
}

// Якорь НОВОГО правила. Для блюда из прошлого — сегодня: «завтра» вчерашнего
// блюда уже прошло, а пользователь, дублируя старое блюдо, имеет в виду
// будущее от сегодняшнего дня. Для сегодняшних и будущих блюд — их дата.
// Офсеты копий при этом всё равно считаются от даты блюда (planRuleChange).
export function effectiveAnchorDate(viewDateStr, todayStr = fmtDate(new Date())) {
  return viewDateStr > todayStr ? viewDateStr : todayStr
}

export function emptyRule(anchorDate) {
  return { today: false, tomorrow: false, weekdays: false, alldays: false, anchor_date: anchorDate }
}

export function ruleHasAny(rule) {
  return !!(rule && (rule.today || rule.tomorrow || rule.weekdays || rule.alldays))
}

// Все даты правила, 'YYYY-MM-DD', отсортированы. Якорь входит только по
// флагу today.
export function ruleDates(rule) {
  if (!rule || !rule.anchor_date) return []
  const out = new Set()
  if (rule.today) out.add(rule.anchor_date)
  if (rule.tomorrow) out.add(fmtDate(addDays(rule.anchor_date, 1)))
  for (let off = 1; off <= 7; off++) {
    const d = addDays(rule.anchor_date, off)
    const dow = d.getDay() // 0 = вс
    if (rule.alldays || (rule.weekdays && dow >= 1 && dow <= 5)) out.add(fmtDate(d))
  }
  return [...out].sort()
}

// Будущие даты правила — то, что ещё предстоит. Всё остальное правило
// описывает уже случившееся и на интерфейс влиять не должно.
export function futureRuleDates(rule, todayStr = fmtDate(new Date())) {
  return ruleDates(rule).filter(d => d > todayStr)
}

// Как показывать правило СЕГОДНЯ: чекбокс горит, только пока у его варианта
// остались будущие даты. Хранимое правило абсолютно (якорь — дата оригинала),
// и без этой поправки «Завтра ✓» продолжало показываться и через неделю после
// того самого завтра — пользователь читал галочку как «повторится и дальше»,
// а сохранение попапа не отправляло ничего (булевы «не изменились»).
export function deriveRuleView(rule, todayStr = fmtDate(new Date())) {
  const view = { today: false, tomorrow: false, weekdays: false, alldays: false }
  if (!rule || !rule.anchor_date) return view
  // «Сегодня» горит ровно в тот день, на который легла копия: назавтра это
  // уже история, и открытая вчерашняя запись снова предлагает повтор
  if (rule.today) {
    view.today = rule.anchor_date === todayStr
  }
  if (rule.tomorrow) {
    view.tomorrow = fmtDate(addDays(rule.anchor_date, 1)) > todayStr
  }
  for (let off = 1; off <= 7; off++) {
    const d = addDays(rule.anchor_date, off)
    if (fmtDate(d) <= todayStr) continue
    const dow = d.getDay()
    if (rule.alldays) view.alldays = true
    if (rule.weekdays && dow >= 1 && dow <= 5) view.weekdays = true
  }
  return view
}

const MONTHS_GEN = ['января', 'февраля', 'марта', 'апреля', 'мая', 'июня',
  'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря']

function fmtHuman(dateStr) {
  const d = parseDate(dateStr)
  return `${d.getDate()} ${MONTHS_GEN[d.getMonth()]}`
}

// Текст индикатора «что ещё будет продублировано»: null, когда будущих копий
// не запланировано. Формулировка — от живых (деривированных) вариантов,
// граница — последняя будущая дата правила.
export function ruleSummary(rule, todayStr = fmtDate(new Date())) {
  const future = futureRuleDates(rule, todayStr)
  if (!future.length) return null
  const last = future[future.length - 1]
  const tomorrowStr = fmtDate(addDays(todayStr, 1))
  if (future.length === 1) {
    return future[0] === tomorrowStr ? 'завтра' : fmtHuman(future[0])
  }
  const view = deriveRuleView(rule, todayStr)
  if (view.alldays) return `каждый день до ${fmtHuman(last)}`
  if (view.weekdays) {
    // «Завтра» упоминается отдельно, только если это настоящее завтра
    // (дата якорь+1 совпадает с ним) и будни его не покрывают
    const ruleTomorrow = rule.tomorrow ? fmtDate(addDays(rule.anchor_date, 1)) : null
    const tomorrowDow = parseDate(tomorrowStr).getDay()
    const tomorrowIsWeekend = tomorrowDow === 0 || tomorrowDow === 6
    return ruleTomorrow === tomorrowStr && tomorrowIsWeekend
      ? `завтра и по будням до ${fmtHuman(last)}`
      : `по будням до ${fmtHuman(last)}`
  }
  return `до ${fmtHuman(last)}`
}

// Подписи-даты и доступность опций попапа для блюда/приёма с датой
// viewDateStr. Даты считаются от якоря, который получит новое правило
// (effectiveAnchorDate): у сегодняшнего и будущего блюда — от его даты,
// у блюда из прошлого — от сегодня, ровно так их посчитает и сохранение.
// visible=false — вариант не показывать: «сегодня» есть только у блюда
// из прошлого. enabled=false — будущих дат у варианта нет.
export function optionDateHints(viewDateStr, todayStr = fmtDate(new Date())) {
  const anchor = effectiveAnchorDate(viewDateStr, todayStr)
  const probe = { anchor_date: anchor }
  const mk = (flag) => {
    const rule = { ...probe, [flag]: true }
    const all = ruleDates(rule)
    const future = futureRuleDates(rule, todayStr)
    const hint = flag === 'tomorrow'
      ? fmtHuman(all[0])
      : `до ${fmtHuman(all[all.length - 1])}`
    return { hint, enabled: future.length > 0, visible: true }
  }
  const isPast = viewDateStr < todayStr
  return {
    today: { hint: fmtHuman(todayStr), enabled: isPast, visible: isPast },
    tomorrow: mk('tomorrow'),
    weekdays: mk('weekdays'),
    alldays: mk('alldays'),
  }
}

// План перехода от oldRule к newRule для POST /diary/duplicate.
// sourceDateStr — дата записей, из которых сервер будет копировать
// (offset считается от неё и может быть отрицательным). Прошлое не трогаем
// ни добавлением, ни удалением; сегодня — только добавлением (вариант
// «сегодня» у блюда из прошлого): сегодняшнюю копию пользователь удаляет
// обычным способом, а вчерашние копии — уже история дневника.
export function planRuleChange(oldRule, newRule, sourceDateStr, todayStr = fmtDate(new Date())) {
  const today = todayStr
  const oldSet = new Set(ruleDates(oldRule))
  const newSet = new Set(ruleDates(newRule))
  const src = parseDate(sourceDateStr)

  const add_offsets = []
  // На сегодня добавляет только явный флаг today; «будни»/«каждый день»
  // старого якоря, зацепившие сегодняшнюю дату, остаются историей
  const todayByFlag = !!(newRule && newRule.today && newRule.anchor_date === today)
  for (const date of newSet) {
    if (oldSet.has(date) || date < today) continue
    if (date === today && !todayByFlag) continue
    const off = Math.round((parseDate(date) - src) / 86400000)
    if (off !== 0) add_offsets.push(off)
  }

  const remove_ranges = []
  for (const date of oldSet) {
    if (newSet.has(date) || date <= today) continue
    const start = parseDate(date)
    const end = addDays(date, 1)
    remove_ranges.push({ start: start.toISOString(), end: end.toISOString() })
  }

  return { add_offsets, remove_ranges }
}
